import { QueryResult, useQuery } from '@apollo/client'
import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import invariant from 'tiny-invariant'
import { gql } from '~/__generated__'
import { Exact, ViewerQuery } from '~/__generated__/graphql'
import { loginPath } from '~/common/paths'
import { GraphqlError } from '~/ui/errors'

interface ViewerContextType {
  result: QueryResult<ViewerQuery, Exact<{ [key: string]: never }>>
  viewer: ViewerQuery['viewer']
}

const ViewerContext = createContext<ViewerContextType | null>(null)

export const viewerQuery = gql(/* GraphQL */ `
  query Viewer {
    viewer {
      id
      ...CachedViewerContext
    }
  }
`)

export const ViewerProvider = ({ children }: { children: React.ReactNode }) => {
  const result = useQuery(viewerQuery, { fetchPolicy: 'cache-first' })
  const viewer = result.data?.viewer

  const value = useMemo(() => ({ result, viewer }), [result, viewer])

  if (result.error) {
    return <GraphqlError error={result.error} />
  }

  return <ViewerContext.Provider value={value}>{children}</ViewerContext.Provider>
}

export const useViewerMaybe = () => {
  const contextValue = useContext(ViewerContext)
  if (contextValue === null) {
    throw Error('useViewer must be used within a ViewerProvider')
  }
  return contextValue
}

export const useViewer = () => {
  const contextValue = useContext(ViewerContext)
  const navigate = useNavigate()

  invariant(contextValue, 'Context has not been Provided!')

  useEffect(() => {
    if (!contextValue.viewer) {
      navigate(loginPath({}))
    }
  }, [contextValue.viewer, navigate])

  if (!contextValue.viewer) {
    return {
      viewer: null as any, // Type assertion to avoid type errors
      refetch: contextValue.result.refetch,
      startPolling: contextValue.result.startPolling,
      stopPolling: contextValue.result.stopPolling,
    }
  }

  return {
    viewer: contextValue.viewer,
    refetch: contextValue.result.refetch,
    startPolling: contextValue.result.startPolling,
    stopPolling: contextValue.result.stopPolling,
  }
}
